import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCalendar } from 'hooks/useCalendar';
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { convertDateToRequestParam, isEqualDate } from 'utils/convertFromLocalDate'
import { validateRequired } from 'utils/validation'

import DuplicateCenterDialog from '../../../dialog/DuplicateCenterDialog/DuplicateCenterDialog'

import type { ColDef, ValueFormatterParams,ValueParserParams,IRichCellEditorParams, GetContextMenuItemsParams, MenuItemDef, DefaultMenuItem } from 'ag-grid-enterprise'
import type { Calendar, MstCalendar, GetMstCalendarCalendarGetRequest, MstCenter, Codes } from 'api-client';
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  "org_center_id": "orgCenterId",
  "calendar_date" : "calendarDate",
  "enable_ship" : "enableShip",
  "enable_order" : "enableOrder",
  "enable_arrival" : "enableArrival",
  "arrival_ratio" : "arrivalRatio",
  "remarks" : "remarks",
  "delete_flg": "deleteFlg"
};

const primayKey = ['orgCenterId', 'calendarDate']

const tableFieldToGroup: Record<string, string> = {
  enableShip: 'extend_enable_flg',
  enableArrival: 'extend_enable_flg',
  enableOrder: 'extend_enable_flg',
}

interface DataListProps extends DataListPropsBase {
  list: Calendar
  onReload: () => void
  searchParam: GetMstCalendarCalendarGetRequest
  center: MstCenter[]
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

/** Row type: 3 enable fields use typeCode (string) from extend_enable_flg */
interface MstCalendarWithId extends Omit<MstCalendar, 'enableShip' | 'enableArrival' | 'enableOrder'>, RecordEditEx {
  enableShip?: string | null
  enableArrival?: string | null
  enableOrder?: string | null
}

function DataList({ 
  list, 
  isLoading, 
  searchParam, 
  center,
  onReload, 
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo, typeCode } = useAuthContext()
  const { fetchCalendarUpdate, result, isUpdating } = useCalendar()
  const internalGridRef = useRef<AgGridReact<MstCalendarWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  const searchParamCurrent = useRef<GetMstCalendarCalendarGetRequest>(searchParam)
  const defaultEnableFlag = typeCode.find((r) => r.typeGroup === 'extend_enable_flg')?.codes.find((r) => r.defaultFlg)?.typeCode || null

  useEffect(() => {
    searchParamCurrent.current = searchParam
  }, [searchParam])

  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [editorDataReady, setEditorDataReady] = useState(false)
  const [row, setRow] = useState<MstCalendarWithId[]>(
    list.list.map((item) => ({ ...item, ...initRecordEditEx() } as MstCalendarWithId))
  )
  const [centerSelect, setCenterSelect] = useState<(string | null)[]>([])
  const [orderSysTypeCodeLists, setOrderSysTypeCodeLists] = useState<{ [key: string]: Codes[] }>({})
  const [orderSysTypeCodeSelects, setOrderSysTypeCodeSelects] = useState<{ [key: string]: (string | null)[] }>({})
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [selectedRowForDuplicate, setSelectedRowForDuplicate] = useState<MstCalendarWithId | null>(null)
  const [selectedCenterIds, setSelectedCenterIds] = useState<string[]>([])

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    const newLists: { [key: string]: Codes[] } = {}
    const newSelects: { [key: string]: (string | null)[] } = {}
    const extendEnableFlgGroup = typeCode?.find((r) => r.typeGroup === 'extend_enable_flg')
    const codes = extendEnableFlgGroup?.codes || []
    newLists.extend_enable_flg = codes
    newSelects.extend_enable_flg = codes.map((r) => r.typeCode)
    setOrderSysTypeCodeLists(newLists)
    setOrderSysTypeCodeSelects(newSelects)
  }, [typeCode])

  useEffect(() => {
    if (centerSelect.length > 0 && center && (orderSysTypeCodeSelects.extend_enable_flg?.length ?? 0) > 0) {
      setEditorDataReady(true)
    }
  }, [centerSelect, center, orderSysTypeCodeSelects])

  useEffect(() => {
    setEditorDataReady(false)
    const values = center?.map((r) => r.orgCenterId) || []
    setCenterSelect([...values])
  }, [center])

  const createBlankRow = useCallback((): MstCalendarWithId => {
    const currentCenter = center?.find(centerExist => centerExist.orgCenterId === searchParamCurrent.current.orgCenterId)
    const currentOrgCenterId = currentCenter?.orgCenterId || null

    return {
      companyId: loginUserInfo?.companyId || null,
      orgCenterId: currentOrgCenterId,
      calendarDate: null,
      enableArrival: defaultEnableFlag,
      enableOrder: defaultEnableFlag,
      enableShip: defaultEnableFlag,
      deleteDatetime: null,
      updateUser: null,
      deleteUser: null,
      insertDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      updateDatetime: null,
      arrivalRatio: null,
      remarks: null,
      deleteFlg: false,
      ...initRecordEditEx(true)
    }
  }, [searchParam, center, loginUserInfo?.companyId, loginUserInfo?.userId])
  
  // 入力チェック
  const fieldValidate = (pRow: MstCalendarWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field){
      case "orgCenterId":
      case "calendarDate":
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break;
      // case "enableShip":
      // case "enableArrival":
      // case "enableOrder":
      //   hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
      //   break;

      default:
        return false;
    }
    return hasErrors
  };
  
  const updateMstCalendar = useCallback(
    async (updatedRows: MstCalendarWithId[]) => {
    const datalist = updatedRows.map(({ isEdited, orgCenterId, calendarDate, editedFields, errors, ...rest }) => ({
      ...rest,
      orgCenterId: orgCenterId ??  '',
      calendarDate: calendarDate ? convertDateToRequestParam(new Date(calendarDate)) : null,
    }));

    try {
      await fetchCalendarUpdate({
        mstCalendarUpdate: { body: datalist as any }
      })
      onReload()
    } catch (err: any) {
      if (err?.errors && err.result?.status === "error") {
        const gridApi = gridRef.current?.api
        err.errors.forEach((errrow: any) => {
          const {rowdata}=errrow
          const orgCenterId = (rowdata as { org_center_id: string | null })?.org_center_id;
          const calendarDate = (rowdata as { calendar_date: string | null})?.calendar_date;
          const errRow = updatedRows.find((rowdatas) => rowdatas.orgCenterId === orgCenterId && isEqualDate(rowdatas.calendarDate,calendarDate)); 
          // console.log("errRow", orgCenterId, calendarDate, errRow, updatedRows)
          if (errRow) {
            // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
            errRow.errors = {
              orgCenterId: [errrow.errorMessage],
              calendarDate: [errrow.errorMessage],
            }
            const rowNode = gridApi?.getRowNode(errRow.id || '')
            if (rowNode) {
              rowNode.setData(errRow)
            }
          }
        })
      }
    }
  }, [fetchCalendarUpdate, searchParam]);

  const handleImportExcel = (data: any[]) => {
    let hasError = false;
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCalendarWithId> = {}

      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue: unknown = importvalue || null
          if (tableField === 'deleteFlg') {
            setvalue = parseBoolean(importvalue)
          } else if (['enableShip', 'enableArrival', 'enableOrder'].indexOf(tableField) >= 0) {
            const group = tableFieldToGroup[tableField]
            const typeList = orderSysTypeCodeLists[group]
            const codevalue = typeCodeParser(importvalue, typeList)
            if (isNonInputValue(importvalue)) {
              setvalue = null
            } else if (codevalue) {
              setvalue = codevalue
            } else {
              setvalue = parseBoolean(importvalue)
            }
          } else if (tableField === 'calendarDate') {
            setvalue = parseDate(importvalue) as unknown as undefined
          } else if (tableField === 'arrivalRatio') {
            setvalue = parseNumericWithRangeZero(importvalue)
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstCalendarWithId] = setvalue as unknown as undefined
        }
      })
      return mappedRow as MstCalendarWithId
    })
    return { mappedData, hasError }
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    dateTreeSetFilterParams,
    clearPinnedRows,
    numberRangeValueParserZero,
    parseNumericWithRangeZero,
    getContextMenuItems: baseGetContextMenuItems,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-calendar',
    updateData: updateMstCalendar,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    // cellValueChanged,
    scrollPositionKey: 'orgCenterId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef)[] => 
  [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '自社センターID',
      field: 'orgCenterId', // 自社センターID
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      width: 350,
      flex:3,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, center, "orgCenterId", "centerName"),
      filterValueGetter: (params) => selectValueFormatter(params.data.orgCenterId, center, "orgCenterId", "centerName"),
      valueParser: (params: ValueParserParams) => selectValueParser(params, center, "orgCenterId"),
      cellEditorParams: editorDataReady ? {
        values : [null, ...centerSelect],
        formatValue: (value: string) => selectValueFormatter(value, center, "orgCenterId", "centerName"),
        searchType: "match",
        allowTyping: true,
        filterList: true,
        highlightMatch: true,
      } as IRichCellEditorParams : {values: ['-']},
    },           
    {
      headerName: '日付',
      field: 'calendarDate', // 日付
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {filter: 'agDateColumnFilter',
            filterParams: {
              comparator: (filterLocalDateAtMidnight: Date, cellValue: any) => {
                const d = parseDate(cellValue)
                if (!d) return -1
                const cell = new Date(d.getFullYear(), d.getMonth(), d.getDate())
                const filter = new Date(
                  filterLocalDateAtMidnight.getFullYear(),
                  filterLocalDateAtMidnight.getMonth(),
                  filterLocalDateAtMidnight.getDate()
                )
                if (cell < filter) return -1
                if (cell > filter) return 1
                return 0
              },
            },
          },
          {
            filter: 'agSetColumnFilter',
            filterParams: dateTreeSetFilterParams,
          },
        ],
        closeOnApply: true,
      },
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => params.data && params.data.insertDatetime === null, 
      },
      width: 300,
      flex:2,
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        minYear: 1900, 
        maxYear: 2050,
      },
      valueFormatter: dateFormatter, 
      valueParser: parseDateValue,
    },
    {
      headerName: '出荷可能',
      field: 'enableShip',
      editable: isEnableEdit(),
      flex: 2,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        typecodeFormatter(
          (params.value !== null && params.value !== undefined) ? String(params.value) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      filterValueGetter: (params) =>
        typecodeFormatter(
          (params.data.enableShip !== null && params.data.enableShip !== undefined) ? String(params.data.enableShip) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      valueParser: (params: ValueParserParams) =>
        selectTypecodeValueParser(params, orderSysTypeCodeLists.extend_enable_flg || []),
      cellEditorParams: editorDataReady ? {
        values: [null, ...(orderSysTypeCodeSelects.extend_enable_flg || [])],
        formatValue: (value: string) =>
          typecodeFormatter(value, orderSysTypeCodeLists.extend_enable_flg || []),
      } as IRichCellEditorParams : { values: ['-'] },
      cellClassRules: getCellClassRules(),
    },
    {
      headerName: '入荷可能',
      field: 'enableArrival',
      editable: isEnableEdit(),
      flex: 2,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        typecodeFormatter(
          (params.value !== null && params.value !== undefined) ? String(params.value) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      filterValueGetter: (params) =>
        typecodeFormatter(
          (params.data.enableArrival !== null && params.data.enableArrival !== undefined) ? String(params.data.enableArrival) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      valueParser: (params: ValueParserParams) =>
        selectTypecodeValueParser(params, orderSysTypeCodeLists.extend_enable_flg || []),
      cellEditorParams: editorDataReady ? {
        values: [null, ...(orderSysTypeCodeSelects.extend_enable_flg || [])],
        formatValue: (value: string) =>
          typecodeFormatter(value, orderSysTypeCodeLists.extend_enable_flg || []),
      } as IRichCellEditorParams : { values: ['-'] },
      cellClassRules: getCellClassRules(),
    },
    {
      headerName: '発注業務可能',
      field: 'enableOrder',
      editable: isEnableEdit(),
      flex: 2,
      cellEditor: 'agRichSelectCellEditor',
      valueFormatter: (params: ValueFormatterParams) =>
        typecodeFormatter(
          (params.value !== null && params.value !== undefined) ? String(params.value) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      filterValueGetter: (params) =>
        typecodeFormatter(
          (params.data.enableOrder !== null && params.data.enableOrder !== undefined) ? String(params.data.enableOrder) : '',
          orderSysTypeCodeLists.extend_enable_flg || []
        ),
      valueParser: (params: ValueParserParams) =>
        selectTypecodeValueParser(params, orderSysTypeCodeLists.extend_enable_flg || []),
      cellEditorParams: editorDataReady ? {
        values: [null, ...(orderSysTypeCodeSelects.extend_enable_flg || [])],
        formatValue: (value: string) =>
          typecodeFormatter(value, orderSysTypeCodeLists.extend_enable_flg || []),
      } as IRichCellEditorParams : { values: ['-'] },
      cellClassRules: getCellClassRules(),
    },
    {
      headerName: '入荷比率設定',
      field: 'arrivalRatio', // 入荷比率設定
      editable: isEnableEdit(),
      flex:1,
      cellClass: 'number-cell',
      filter: 'agNumberColumnFilter',
      cellEditor:'agNumberCellEditor',
      valueFormatter: numberValueFormatter,
      valueParser: numberRangeValueParserZero, 
    },
    {
      headerName: '備考',
      field: 'remarks', 
      flex: 2,
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
    },
    {
      headerName: '削除',
      field: 'deleteFlg', // 削除
      editable: isEnableEdit(),
      flex:1,
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue, 
    },
  ];
  const columnDefs = useMemo<(ColDef)[]>(updateColumnDefs, [editorDataReady, orderSysTypeCodeLists, orderSysTypeCodeSelects])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        { headerName: '自社センターID', children: [{ headerName: 'org_center_id', field: 'orgCenterId', cellClass: 'stringType' }] },
        { headerName: '日付', children: [{ headerName: 'calendar_date', field: 'calendarDate' }] },
        { headerName: '出荷可能', children: [{ headerName: 'enable_ship', field: 'enableShip', cellClass: 'stringType' }] },
        { headerName: '入荷可能', children: [{ headerName: 'enable_arrival', field: 'enableArrival', cellClass: 'stringType' }] },
        { headerName: '発注業務可能', children: [{ headerName: 'enable_order', field: 'enableOrder', cellClass: 'stringType' }] },
        { headerName: '入荷比率設定', children: [{ headerName: 'arrival_ratio', field: 'arrivalRatio', cellClass: 'numberType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
      ])
  }, [setDownloadColumnDefs])

  // Normalize boolean from API to typeCode (string) for extend_enable_flg dropdown
  const extendEnableFlgCodes = useMemo(() => {
    const group = typeCode?.find((r) => r.typeGroup === 'extend_enable_flg')
    const codes = group?.codes || []
    const enabledCode = codes.find((c) => c.defaultFlg)?.typeCode ?? codes[0]?.typeCode ?? '1'
    const disabledCode = codes.find((c) => !c.defaultFlg && c.typeCode !== enabledCode)?.typeCode ?? codes[1]?.typeCode ?? '0'
    return { enabledCode, disabledCode }
  }, [typeCode])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => {
        const rowItem = { ...item, ...initRecordEditEx() } as MstCalendarWithId
        if (typeof (item as any).enableShip === 'boolean') {
          rowItem.enableShip = (item as any).enableShip ? extendEnableFlgCodes.enabledCode : extendEnableFlgCodes.disabledCode
        }
        if (typeof (item as any).enableArrival === 'boolean') {
          rowItem.enableArrival = (item as any).enableArrival ? extendEnableFlgCodes.enabledCode : extendEnableFlgCodes.disabledCode
        }
        if (typeof (item as any).enableOrder === 'boolean') {
          rowItem.enableOrder = (item as any).enableOrder ? extendEnableFlgCodes.enabledCode : extendEnableFlgCodes.disabledCode
        }
        return rowItem
      }))
      clearPinnedRows()
    }
  }, [list.list, createBlankRow, extendEnableFlgCodes])

  // available centers
  const availableCenters = useMemo(() => {
    if (!center || !selectedRowForDuplicate) {
      return center || []
    }
    return center.filter(c => c.orgCenterId !== selectedRowForDuplicate.orgCenterId)
  }, [center, selectedRowForDuplicate])

  // open duplicate dialog
  const handleOpenDuplicateDialog = useCallback((rowData: MstCalendarWithId) => {
    setSelectedRowForDuplicate(rowData)
    setSelectedCenterIds([])
    setDuplicateDialogOpen(true)
  }, [])

  // close duplicate dialog
  const handleCloseDuplicateDialog = useCallback(() => {
    setDuplicateDialogOpen(false)
    setSelectedRowForDuplicate(null)
    setSelectedCenterIds([])
  }, [])

  // select all centers
  const handleSelectAllCenters = useCallback((checked: boolean) => {
    if (!availableCenters || availableCenters.length === 0) return
    if (checked) {
      const allCenterIds = availableCenters.map(c => c.orgCenterId || '').filter(id => id !== '')
      setSelectedCenterIds(allCenterIds)
    } else {
      setSelectedCenterIds([])
    }
  }, [availableCenters])

  // toggle select one center
  const handleToggleCenter = useCallback((centerId: string) => {
    setSelectedCenterIds(prev => {
      if (prev.includes(centerId)) {
        return prev.filter(id => id !== centerId)
      }
      return [...prev, centerId]
    })
  }, [])

  // duplicate record to new center
  const handleDuplicateToCenter = useCallback(() => {
    if (!selectedRowForDuplicate || selectedCenterIds.length === 0 || !gridRef.current) {
      return
    }

    const { api } = gridRef.current
    if (!api) return

    // create new records, one record for each selected center
    const { id, ...restData } = selectedRowForDuplicate
    const newRows: MstCalendarWithId[] = selectedCenterIds.map(centerId => ({
      ...restData,
      orgCenterId: centerId,
      calendarDate: selectedRowForDuplicate.calendarDate, // Giữ nguyên ngày
      companyId: loginUserInfo?.companyId || null,
      insertUser: loginUserInfo?.userId || null,
      insertDatetime: null,
      updateUser: null,
      updateDatetime: null,
      deleteUser: null,
      deleteDatetime: null,
      ...initRecordEditEx(true), // mark as new record
      // mark all cells as edited
      isEdited: true,
      editedFields: new Set(Object.values(columnMapping)),
      errors: {},
    }))

    // add all new rows to grid
    api.applyTransaction({ add: newRows })
    setHasGridEdited(true)
    handleCloseDuplicateDialog()
  }, [selectedRowForDuplicate, selectedCenterIds, gridRef, loginUserInfo, setHasGridEdited, handleCloseDuplicateDialog, columnMapping])

  // custom context menu with duplicate item
  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
    const baseMenu = baseGetContextMenuItems(params)
    
    const { node } = params
    if (!node || !node.data) {
      return baseMenu
    }

    const rowData = node.data as MstCalendarWithId
    
    // add duplicate item after last separator
    const duplicateMenuItem: MenuItemDef = {
      name: '選択した設定を他のセンターに複製',
      action: () => handleOpenDuplicateDialog(rowData),
      disabled: !isEnableEdit() || !rowData.orgCenterId || !rowData.calendarDate,
    }

    // find last separator index and insert new item before it
    const lastSeparatorIndex = baseMenu.lastIndexOf('separator')
    if (lastSeparatorIndex >= 0) {
      const newMenu = [...baseMenu]
      newMenu.splice(lastSeparatorIndex, 0, duplicateMenuItem, 'separator')
      return newMenu
    }

    // if no separator, add to the end
    return [...baseMenu, 'separator', duplicateMenuItem]
  }, [baseGetContextMenuItems, handleOpenDuplicateDialog, isEnableEdit])

  return (
    <>
      <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '69vh' }}>
        <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
          <div style={gridStyle} className="ag-theme-balham">
            <AgGridReact
              key={`${row[0]?.companyId}-${editorDataReady}`}
              rowData={row}
              ref={gridRef}
              getRowId={(params) => params.data.id}
              columnDefs={columnDefs}
              onGridReady={onGridReady}
              localeText={localeText}
              // autoSizeStrategy={autoSizeStrategy}
              loadingOverlayComponent={LoadingOverlay}
              findSearchValue={externalFindSearchValue}
              onFindChanged={externalOnFindChanged}
              getContextMenuItems={getContextMenuItems}
              stopEditingWhenCellsLoseFocus
              />
          </div>
        </div>
      </div>

      <DuplicateCenterDialog
        open={duplicateDialogOpen}
        onClose={handleCloseDuplicateDialog}
        onConfirm={handleDuplicateToCenter}
        availableCenters={availableCenters}
        selectedCenterIds={selectedCenterIds}
        onSelectAll={handleSelectAllCenters}
        onToggleCenter={handleToggleCenter}
        selectedRow={selectedRowForDuplicate}
        formatCenter={(centerId) => selectValueFormatter(centerId, center, "orgCenterId", "centerName")}
        formatDate={(dateValue) => dateFormatter({ value: dateValue } as any)}
      />
    </>
  );
}

export default DataList;
